/**
 * ChatService — Singleton bridging network events, Dexie DB, and UI
 *
 * Listens to incoming chat messages from NetworkManager, persists to local
 * IndexedDB via Dexie, and emits events for UI components to consume.
 *
 * Photos: the sender uploads a downscaled JPEG to the CDN and sends a normal
 * 'image' message carrying the URL. Each side stores the actual bytes in its
 * local DB (the sender straight from the picker, the receiver by downloading
 * once) — and the receiver then acks so the server deletes the CDN copy.
 * Chat history lives on the phones, not our storage.
 */

import { EventEmitter } from '../engine/core/EventEmitter';
import type { NetworkManager } from '../network/NetworkManager';
import type { ChatMessagePayload, ChatMessageType } from '@sdl/shared';
import { showToast } from '../ui/components/Toast';
import { uploadChatImage } from '../api/chatImageApi';
import { pickChatPhoto, ChatPhotoTooLargeError } from '../ui/components/crew/chatPhoto';
import { confirmChatPhoto } from '../ui/components/crew/ChatPhotoPreview';
import {
  getConversations,
  getMessages,
  saveMessage,
  deleteConversation,
  markConversationRead,
  getUnreadCount,
  getChatStorageStats,
  evictOldestImageBlobs,
  clearAllChatData,
  attachImageBlob,
  type ChatStorageStats,
  type StoredMessage,
  type Conversation,
} from '../db/ChatDatabase';

interface ChatServiceEvents {
  'message:received': StoredMessage;
  /** A stored message changed in place (photo blob landed / was evicted). */
  'message:updated': StoredMessage;
  'conversations:updated': Conversation[];
  'unread:changed': { count: number };
}

/** Soft cap on locally stored photo bytes. Past it, the oldest photos are
 *  evicted (their text bubble stays, showing a placeholder). */
const IMAGE_CACHE_CAP_BYTES = 200 * 1024 * 1024;
/** After a cap breach, evict down to this fraction of the cap. */
const EVICT_TO_FRACTION = 0.9;

export class ChatService extends EventEmitter<ChatServiceEvents> {
  private networkManager: NetworkManager;
  private localPlayerId: string;
  private _unreadCount = 0;
  /** JWT provider, injected by main.ts (needed for the photo upload REST call). */
  private getToken: () => string | null = () => null;
  /** Peers opened for chat but with no message yet. Rendered in sidebars so a
   *  freshly selected player appears immediately; dropped on close if the
   *  conversation never produced a message. */
  private drafts = new Map<string, { peerId: string; username: string; picture: string }>();
  /** Message ids currently being downloaded (dedup for the lazy retry path). */
  private fetchingImages = new Set<string>();
  private photoBusy = false;

  constructor(networkManager: NetworkManager, localPlayerId: string) {
    super();
    this.networkManager = networkManager;
    this.localPlayerId = localPlayerId;
    this.setupListeners();
    this.refreshUnreadCount();
  }

  get unreadCount(): number {
    return this._unreadCount;
  }

  /** Inject the live JWT getter (main.ts, after auth:success). */
  setTokenProvider(getToken: () => string | null): void {
    this.getToken = getToken;
  }

  /**
   * Send a text/emoji message
   */
  async sendMessage(to: string, toUsername: string, toPicture: string, content: string, type: ChatMessageType = 'text'): Promise<void> {
    const msg: StoredMessage = {
      id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      localPlayerId: this.localPlayerId,
      peerId: to,
      from: this.localPlayerId,
      fromUsername: '', // Will be filled by server echo
      content,
      type,
      timestamp: Date.now(),
      read: 1, // Own messages are always read
    };

    // Save locally immediately (optimistic)
    await saveMessage(this.localPlayerId, msg, toUsername, toPicture);
    this.drafts.delete(to); // persisted now — no longer a draft
    this.emit('message:received', msg);
    this.notifyConversationsUpdated();

    // Send to server
    this.networkManager.sendChatMessage(to, content, type);
  }

  /**
   * Full photo flow: picker → downscale → CDN upload → image message.
   * Silently no-ops on picker cancel; toasts on failures.
   */
  async sendPhoto(to: string, toUsername: string, toPicture: string): Promise<void> {
    if (this.photoBusy) return;
    const token = this.getToken();
    if (!token) return;

    this.photoBusy = true;
    try {
      const dataUrl = await pickChatPhoto();
      if (!dataUrl) return; // cancelled

      // Preview with CANCEL / SEND — nothing leaves the phone until confirmed.
      const confirmed = await confirmChatPhoto(dataUrl);
      if (!confirmed) return;

      const { url } = await uploadChatImage(dataUrl, to, token);

      const msg: StoredMessage = {
        id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        localPlayerId: this.localPlayerId,
        peerId: to,
        from: this.localPlayerId,
        fromUsername: '',
        content: url,
        type: 'image',
        timestamp: Date.now(),
        read: 1,
      };
      // The sender already holds the bytes — store them locally so this photo
      // never depends on the (soon-deleted) CDN copy.
      const blob = await (await fetch(dataUrl)).blob();
      msg.imageBlob = blob;
      msg.imageBytes = blob.size;
      msg.imageEvicted = 0;

      await this.saveWithQuotaGuard(msg, toUsername, toPicture);
      this.drafts.delete(to);
      this.emit('message:received', msg);
      this.notifyConversationsUpdated();
      void this.enforceImageCap();

      this.networkManager.sendChatMessage(to, url, 'image', url);
    } catch (err) {
      if (err instanceof ChatPhotoTooLargeError) {
        showToast(err.message, { type: 'error' });
      } else {
        console.warn('[chat] photo send failed:', err);
        showToast('Photo could not be sent. Try again.', { type: 'error' });
      }
    } finally {
      this.photoBusy = false;
    }
  }

  /**
   * Send a game invite message
   */
  async sendInvite(
    to: string,
    toUsername: string,
    toPicture: string,
    inviteData: { gameType: 'ceelo' | 'craps'; stakes: string; roomId?: string },
  ): Promise<void> {
    const content = `Game invite: ${inviteData.gameType.toUpperCase()} (${inviteData.stakes})`;
    const msg: StoredMessage = {
      id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      localPlayerId: this.localPlayerId,
      peerId: to,
      from: this.localPlayerId,
      fromUsername: '',
      content,
      type: 'invite',
      timestamp: Date.now(),
      read: 1,
    };

    await saveMessage(this.localPlayerId, msg, toUsername, toPicture);
    this.drafts.delete(to);
    this.emit('message:received', msg);
    this.notifyConversationsUpdated();

    this.networkManager.sendChatMessage(to, content, 'invite', undefined, inviteData);
  }

  // ─── Draft conversations ────────────────────────────────────────────

  /**
   * Register a peer the player just opened a chat with. They appear in the
   * sidebar immediately; if no message is ever exchanged, clearEmptyDrafts()
   * (called on widget close / screen leave) removes them again.
   */
  openDraft(peerId: string, username: string, picture: string): void {
    if (!peerId || peerId === this.localPlayerId) return;
    this.drafts.set(peerId, { peerId, username: username || 'Player', picture: picture || '' });
    this.notifyConversationsUpdated();
  }

  /** Drop drafts that never produced a message. */
  async clearEmptyDrafts(): Promise<void> {
    if (this.drafts.size === 0) return;
    const stored = await getConversations(this.localPlayerId);
    const persisted = new Set(stored.map(c => c.peerId));
    let removed = false;
    for (const peerId of [...this.drafts.keys()]) {
      // Persisted (a message landed) → the draft entry is redundant either way.
      this.drafts.delete(peerId);
      if (!persisted.has(peerId)) removed = true;
    }
    if (removed) this.notifyConversationsUpdated();
  }

  async getConversations(): Promise<Conversation[]> {
    const stored = await getConversations(this.localPlayerId);
    if (this.drafts.size === 0) return stored;
    // Drafts that got a message are now persisted — drop the shadow entry.
    const persisted = new Set(stored.map(c => c.peerId));
    const draftConvos: Conversation[] = [];
    for (const d of this.drafts.values()) {
      if (persisted.has(d.peerId)) {
        this.drafts.delete(d.peerId);
        continue;
      }
      draftConvos.push({
        localPlayerId: this.localPlayerId,
        peerId: d.peerId,
        peerUsername: d.username,
        peerPicture: d.picture,
        lastMessageContent: '',
        lastMessageType: 'text',
        lastMessageTimestamp: Date.now(),
        unreadCount: 0,
      });
    }
    // Fresh drafts sit on top — they're what the player just opened.
    return [...draftConvos, ...stored];
  }

  async getMessages(peerId: string, limit?: number, beforeTimestamp?: number): Promise<StoredMessage[]> {
    const messages = await getMessages(this.localPlayerId, peerId, limit, beforeTimestamp);
    // Lazy repair: any received photo that never finished downloading (e.g.
    // bad network at delivery time) gets another attempt while its CDN copy
    // still exists (the copy is only deleted after our ack).
    for (const msg of messages) {
      if (msg.type === 'image' && !msg.imageBlob && msg.imageEvicted !== 1 && msg.from !== this.localPlayerId) {
        void this.downloadAndStoreImage(msg);
      }
    }
    return messages;
  }

  async deleteConversation(peerId: string): Promise<void> {
    await deleteConversation(this.localPlayerId, peerId);
    this.drafts.delete(peerId);
    this.notifyConversationsUpdated();
  }

  async markRead(peerId: string): Promise<void> {
    await markConversationRead(this.localPlayerId, peerId);
    await this.refreshUnreadCount();
  }

  async refreshUnreadCount(): Promise<void> {
    this._unreadCount = await getUnreadCount(this.localPlayerId);
    this.emit('unread:changed', { count: this._unreadCount });
  }

  // ─── Storage accounting ─────────────────────────────────────────────

  async getStorageStats(): Promise<ChatStorageStats> {
    return getChatStorageStats(this.localPlayerId);
  }

  /** Wipe all local chat history (Settings action). */
  async clearAllChats(): Promise<void> {
    await clearAllChatData(this.localPlayerId);
    this.drafts.clear();
    await this.refreshUnreadCount();
    this.notifyConversationsUpdated();
  }

  dispose(): void {
    this.removeAllListeners();
  }

  private setupListeners(): void {
    // Listen for incoming chat messages from the network
    this.networkManager.on('chat:message' as any, async (data: ChatMessagePayload) => {
      // Skip echo of own messages (server sends back with _echo flag)
      if ((data as any)._echo) return;

      const peerId = data.from;
      const msg: StoredMessage = {
        id: data.id,
        localPlayerId: this.localPlayerId,
        peerId,
        from: data.from,
        fromUsername: data.fromUsername,
        fromPicture: data.fromPicture,
        content: data.content,
        type: data.type,
        timestamp: data.timestamp,
        read: 0, // Incoming messages start as unread
      };

      await saveMessage(this.localPlayerId, msg, data.fromUsername, data.fromPicture ?? '');
      this.drafts.delete(peerId);
      // Refresh the unread count BEFORE emitting so listeners (e.g. the chat
      // bubble) read the fresh count synchronously off `unreadCount` rather than
      // the pre-message value. refreshUnreadCount also emits 'unread:changed'.
      await this.refreshUnreadCount();
      this.emit('message:received', msg);
      this.notifyConversationsUpdated();

      // Photos: pull the bytes down NOW while the CDN copy exists, store them
      // locally, then ack so the server deletes the copy.
      if (data.type === 'image') {
        void this.downloadAndStoreImage(msg);
      }
    });
  }

  /**
   * Download a received photo into local storage, then ack the delivery so
   * the server deletes the CDN copy. Idempotent per message; safe to re-run
   * for messages whose download previously failed.
   */
  private async downloadAndStoreImage(msg: StoredMessage): Promise<void> {
    const url = msg.content;
    if (!url || !/^https?:\/\//.test(url)) return;
    if (this.fetchingImages.has(msg.id)) return;
    this.fetchingImages.add(msg.id);
    try {
      const resp = await fetch(url);
      if (!resp.ok) return; // CDN copy gone (already acked before?) — bubble keeps the URL
      const blob = await resp.blob();

      try {
        await attachImageBlob(msg.id, blob);
      } catch (err) {
        // Device storage full — reclaim space from the oldest photos, retry once.
        await evictOldestImageBlobs(this.localPlayerId, Math.max(blob.size * 4, 20 * 1024 * 1024));
        await attachImageBlob(msg.id, blob);
      }

      msg.imageBlob = blob;
      msg.imageBytes = blob.size;
      msg.imageEvicted = 0;
      this.emit('message:updated', msg);

      // Stored safely → the CDN copy can go.
      this.networkManager.sendChatImageStored(url);
      void this.enforceImageCap();
    } catch (err) {
      console.warn('[chat] photo download failed (will retry on next open):', err);
    } finally {
      this.fetchingImages.delete(msg.id);
    }
  }

  /** Save a message, evicting old photo blobs once if the write hits quota. */
  private async saveWithQuotaGuard(msg: StoredMessage, peerUsername: string, peerPicture: string): Promise<void> {
    try {
      await saveMessage(this.localPlayerId, msg, peerUsername, peerPicture);
    } catch (err) {
      await evictOldestImageBlobs(this.localPlayerId, Math.max((msg.imageBytes ?? 0) * 4, 20 * 1024 * 1024));
      await saveMessage(this.localPlayerId, msg, peerUsername, peerPicture);
    }
  }

  /** Keep locally stored photo bytes under the soft cap (oldest evicted first). */
  private async enforceImageCap(): Promise<void> {
    try {
      const stats = await getChatStorageStats(this.localPlayerId);
      if (stats.imageBytes > IMAGE_CACHE_CAP_BYTES) {
        const target = Math.floor(IMAGE_CACHE_CAP_BYTES * EVICT_TO_FRACTION);
        await evictOldestImageBlobs(this.localPlayerId, stats.imageBytes - target);
      }
    } catch {
      // Accounting must never break messaging.
    }
  }

  private async notifyConversationsUpdated(): Promise<void> {
    const convos = await this.getConversations();
    this.emit('conversations:updated', convos);
  }
}
